import { Globe } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useLanguage } from '@/components/LanguageContext'
import { LANGUAGES, LANGUAGE_FLAGS } from '@/lib/languages'

/**
 * A lista exibida é o REGISTRO de idiomas (`@/lib/languages`), não um array
 * próprio. Manter um array local aqui foi o que deixou o japonês fora do
 * seletor: `/ja` existia, mas só quem digitasse a URL chegava lá. Derivar
 * garante que todo idioma suportado é alcançável pela interface — e o teste
 * `LanguageSelector.test.tsx` falha se essa relação se romper.
 *
 * Os rótulos são endônimos (`日本語`, `Português`) porque quem procura o
 * próprio idioma numa lista não lê a língua da página atual (rubrica C3).
 */

interface LanguageSelectorProps {
  variant?: 'ghost' | 'outline'
}

export default function LanguageSelector({ variant = 'outline' }: LanguageSelectorProps) {
  const { language, setLanguage, t } = useLanguage()

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {/* O gatilho mostra só bandeira + globo; sem nome acessível ele seria
            anunciado como "botão" e vazio. O rótulo vem de `nav.language` via
            `t()`, então acompanha o idioma da interface: 「言語」 em /ja,
            "Idioma" em PT. A LISTA continua derivando de `LANGUAGES` — só o
            nome acessível do gatilho é traduzido. */}
        <Button
          variant={variant}
          size="sm"
          className="gap-2"
          data-testid="language-selector"
          aria-label={t('nav.language')}
        >
          <span className="text-xl leading-none">{LANGUAGE_FLAGS[language]}</span>
          <Globe className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {LANGUAGES.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            lang={lang.code}
            onClick={() => setLanguage(lang.code)}
            className={language === lang.code ? 'bg-accent' : ''}
          >
            <span className="text-lg mr-2">{lang.flag}</span>
            {lang.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
